// shared-components/services/AnnouncementService.js

import { supabase } from "@shared/core/services/SupabaseService";
import { activityLogService } from "../monitoring/ActivityLogService.js";

class AnnouncementService {
  constructor() {
    this.websiteId = null;
  }

  setWebsiteId(websiteId) {
    this.websiteId = websiteId;
  }

  getWebsiteId() {
    if (!this.websiteId) {
      this.websiteId = import.meta.env.VITE_WEBSITE_ID;
    }
    return this.websiteId;
  }

  // Generate announcement content from activity
  generateFromActivity(activity) {
    const templates = {
      tour_date: {
        created: {
          title: "🎫 New Event Added",
          message: `New performance at ${activity.resource_name} has been added to our tour dates.`,
          link_url: "/tour",
          announcement_type: "event_update",
          style: "info",
        },
        updated: {
          title: "📍 Event Updated",
          message: `Important update for ${activity.resource_name} - please check the new details.`,
          link_url: "/tour",
          announcement_type: "event_update",
          style: "warning",
        },
        status_changed: {
          title: "🔄 Event Status Changed",
          message: `${activity.resource_name} status has changed${activity.details?.new_status ? ` to ${activity.details.new_status}` : ""}.`,
          link_url: "/tour",
          announcement_type: "event_update",
          style: "warning",
        },
      },
      product: {
        created: {
          title: "🛍️ New in Shop",
          message: `"${activity.resource_name}" is now available in our shop!`,
          link_url: "/shop",
          announcement_type: "new_product",
          style: "success",
        },
      },
      gallery_image: {
        created: {
          title: "📸 New Gallery Image",
          message: `New photo added to the gallery`,
          link_url: "/gallery",
          announcement_type: "new_media",
          style: "success",
        },
      },
    };

    return templates[activity.resource_type]?.[activity.action] || null;
  }

  // Create announcement from activity
  async createFromActivity(activityId, customizations = {}) {
    try {
      // First, fetch the activity
      const { data: activity, error: activityError } = await supabase
        .from("activity_logs")
        .select("*")
        .eq("id", activityId)
        .single();

      if (activityError) throw activityError;

      // Generate announcement content
      const generated = this.generateFromActivity(activity);
      if (!generated) {
        throw new Error("Cannot generate announcement for this activity type");
      }

      // Merge with customizations
      const announcement = {
        website_id: this.getWebsiteId(),
        activity_log_id: activityId,
        ...generated,
        ...customizations,
        is_active: true,
        is_dismissible: true,
        priority: customizations.priority || 0,
      };

      const { data, error } = await supabase
        .from("announcements")
        .insert([announcement])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error creating announcement from activity:", error);
      throw error;
    }
  }

  // Create manual announcement
  async create(announcement) {
    try {
      // Clean up empty date strings
      const cleanedAnnouncement = {
        website_id: this.getWebsiteId(),
        ...announcement,
        // Convert empty strings to null for date fields
        start_date: announcement.start_date || null,
        end_date: announcement.end_date || null,
      };

      const { data, error } = await supabase
        .from("announcements")
        .insert([cleanedAnnouncement])
        .select()
        .single();

      if (error) throw error;

      await activityLogService.logCreate("announcement", data.id, data.title, {
        type: data.announcement_type,
      });

      return data;
    } catch (error) {
      console.error("Error creating announcement:", error);
      throw error;
    }
  }

  // Get active announcements
  async getActive() {
    try {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .eq("website_id", this.getWebsiteId())
        .eq("is_active", true)
        .or(`start_date.is.null,start_date.lte.${new Date().toISOString()}`)
        .or(`end_date.is.null,end_date.gt.${new Date().toISOString()}`)
        .order("priority", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching announcements:", error);
      return [];
    }
  }

  // Get all announcements (including inactive)
  async getAll() {
    try {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .eq("website_id", this.getWebsiteId())
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching all announcements:", error);
      throw error;
    }
  }

  // Toggle active state
  async toggleActive(id) {
    try {
      // First get current state
      const { data: current, error: fetchError } = await supabase
        .from("announcements")
        .select("is_active")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      // Toggle it
      const { data, error } = await supabase
        .from("announcements")
        .update({ is_active: !current.is_active })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error toggling announcement:", error);
      throw error;
    }
  }

  // Create simple announcement (quick mode)
  async createSimple(title, message, type = "general") {
    return this.create({
      title,
      message,
      announcement_type: type,
      style: "info",
      is_active: true,
      is_dismissible: true,
      priority: 0,
    });
  }

  // Clear old announcements (older than X days)
  async clearOld(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const { error } = await supabase
        .from("announcements")
        .delete()
        .eq("website_id", this.getWebsiteId())
        .lt("created_at", cutoffDate.toISOString());

      if (error) throw error;
      return true;
    } catch (error) {
      console.error("Error clearing old announcements:", error);
      throw error;
    }
  }

  // Update announcement
  async update(id, updates) {
    try {
      // Clean up empty date strings
      const cleanedUpdates = {
        ...updates,
        start_date: updates.start_date || null,
        end_date: updates.end_date || null,
      };

      const { data, error } = await supabase
        .from("announcements")
        .update(cleanedUpdates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      await activityLogService.logUpdate("announcement", id, data.title, {
        updated_fields: Object.keys(updates),
      });

      return data;
    } catch (error) {
      console.error("Error updating announcement:", error);
      throw error;
    }
  }

  // Delete announcement
  async delete(id) {
    try {
      // Get announcement details first
      const { data: announcement } = await supabase
        .from("announcements")
        .select("title")
        .eq("id", id)
        .single();

      const { error } = await supabase
        .from("announcements")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await activityLogService.logDelete(
        "announcement",
        id,
        announcement?.title || "Unknown",
      );
    } catch (error) {
      console.error("Error deleting announcement:", error);
      throw error;
    }
  }
}

export const announcementService = new AnnouncementService();
